import type { Translations } from "../client/i18n/translations";

export const getMoodEmoji = (mood: string | null | undefined): string => {
  switch (mood) {
    case "excellent": return "😊";
    case "good":      return "🙂";
    case "neutral":   return "😐";
    case "poor":      return "😕";
    case "bad":       return "😞";
    default:          return "—";
  }
};

export const getPerformanceColor = (performance: string | null | undefined): string => {
  switch (performance) {
    case "excellent": return "text-pnlPositive";
    case "good":      return "text-muted-foreground";
    case "average":   return "text-[hsl(var(--warning))]";
    case "poor":      return "text-pnlNegative";
    default:          return "text-muted-foreground";
  }
};

export const getPerformanceLabel = (
  performance: string | null | undefined,
  t: Translations,
): string => {
  switch (performance) {
    case "excellent": return t.analytics.excellent;
    case "good":      return t.analytics.good;
    case "average":   return t.analytics.average;
    case "poor":      return t.analytics.poor;
    default:          return "—";
  }
};

export const getMoodLabel = (
  mood: string | null | undefined,
  t: Translations,
): string => {
  switch (mood) {
    case "excellent": return t.analytics.excellent;
    case "good":      return t.analytics.good;
    case "neutral":   return t.analytics.neutral;
    case "poor":      return t.analytics.poor;
    case "bad":       return t.analytics.bad;
    default:          return "—";
  }
};

/** Format a duration in minutes to a human-readable string, e.g. "1h 30min" or "45min". */
export const formatSessionDuration = (minutes: number | null | undefined): string => {
  if (!minutes) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
};

/**
 * Extracts the 11-character YouTube video ID from a YouTube URL.
 * Accepts: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID
 * Returns null for invalid or unrecognized URLs.
 */
export const extractYouTubeId = (url: string): string | null => {
  const match = url.match(
    /^https:\/\/(?:www\.youtube\.com\/watch\?v=|youtu\.be\/|www\.youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})([&?].*)?$/,
  );
  return match ? match[1] : null;
};

/**
 * A trade belongs to a session when its ENTRY time falls within the session
 * window. We deliberately key off the entry (not the exit): a trade opened
 * during the session is part of it even if it closes long after `endedAt`
 * (e.g. a short opened at 16:24 and covered at 20:00 still belongs to the
 * 16:24 session). Keying off the exit would orphan such trades.
 */
export function isTradeInSession(
  trade: { date: Date | string },
  session: { startedAt: Date | string | null; endedAt: Date | string | null },
): boolean {
  if (!session.startedAt || !session.endedAt) return false;
  const entry = new Date(trade.date).getTime();
  return entry >= new Date(session.startedAt).getTime() && entry <= new Date(session.endedAt).getTime();
}

/**
 * Chart window for a session: starts at `startedAt` and ends at the LATEST of
 * `endedAt` and every in-session trade's exit. This keeps a late-closing trade
 * (exit after `endedAt`) fully visible on the chart instead of clipping its
 * exit marker. `trades` should already be the session's trades.
 */
export function sessionChartWindow(
  session: { startedAt: Date | string | null; endedAt: Date | string | null },
  trades: Array<{ date: Date | string; exitDate?: Date | string | null }>,
): { startedAt: Date | null; endedAt: Date | null } {
  if (!session.startedAt || !session.endedAt) return { startedAt: null, endedAt: null };
  const start = new Date(session.startedAt);
  let endMs = new Date(session.endedAt).getTime();
  for (const tr of trades) {
    const exit = tr.exitDate ?? tr.date;
    if (exit) endMs = Math.max(endMs, new Date(exit).getTime());
  }
  return { startedAt: start, endedAt: new Date(endMs) };
}

/**
 * Single shared style for error pastilles. Red-tinted, readable in dark and
 * light mode. Errors are semantically negative, so they all share one style.
 */
export const errorPastilleClass =
  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium " +
  "bg-red-500/15 text-red-600 dark:text-red-300 border border-red-500/30";
